import React, { useEffect, useMemo } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import {
  MessageBubbleWidgetProps,
  MessageBubbleWidgetState,
  ElementStyle,
  StylableElement,
} from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import {
  FONT_WEIGHT_OPTIONS_NUMERIC,
  FONT_WEIGHT_OPTIONS_NORMAL_TO_SEMIBOLD,
} from "../../../../utils/fontWeightOptions";
import { themeColor } from "../../../../types/color";
import { ResolveColorFn } from "../../../../hooks/useColorResolver";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

/**
 * Default element styles providing a sensible typography hierarchy
 * Elements with color editing in the panel get themeColor("textPrimary") as default
 */
const DEFAULT_ELEMENT_STYLES: Record<StylableElement, ElementStyle> = {
  // Headings - decreasing sizes with bold weight
  h1: {
    fontSize: 28,
    fontWeight: "700",
    lineHeight: 1.3,
    marginTop: 16,
    marginBottom: 12,
    color: themeColor("textPrimary"),
  },
  h2: {
    fontSize: 24,
    fontWeight: "700",
    lineHeight: 1.35,
    marginTop: 14,
    marginBottom: 10,
    color: themeColor("textPrimary"),
  },
  h3: {
    fontSize: 20,
    fontWeight: "600",
    lineHeight: 1.4,
    marginTop: 12,
    marginBottom: 8,
    color: themeColor("textPrimary"),
  },
  h4: {
    fontSize: 18,
    fontWeight: "600",
    lineHeight: 1.4,
    marginTop: 10,
    marginBottom: 6,
  },
  h5: {
    fontSize: 16,
    fontWeight: "600",
    lineHeight: 1.4,
    marginTop: 8,
    marginBottom: 4,
  },
  // Text elements
  p: {
    fontSize: 16,
    fontWeight: "500",
    lineHeight: 1.6,
    marginTop: 0,
    marginBottom: 12,
    color: themeColor("textPrimary"),
  },
  strong: { fontWeight: "600" },
  em: { fontWeight: "500" },
  code: { fontSize: 14, fontWeight: "500", lineHeight: 1.4 },
  blockquote: {
    fontSize: 16,
    fontWeight: "500",
    lineHeight: 1.6,
    marginTop: 8,
    marginBottom: 8,
    color: themeColor("textPrimary"),
  },
  // Lists
  ul: { marginTop: 8, marginBottom: 12 },
  ol: { marginTop: 8, marginBottom: 12 },
  li: {
    fontSize: 16,
    fontWeight: "500",
    lineHeight: 1.6,
    marginBottom: 4,
    color: themeColor("textPrimary"),
  },
  // Tables
  table: { fontSize: 14, marginTop: 8, marginBottom: 12 },
  thead: {
    fontWeight: "600",
    backgroundColor: themeColor("primary"),
    color: themeColor("primaryForeground"),
  },
  tbody: { fontWeight: "500" },
  tr: {},
  th: {
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 1.4,
    color: themeColor("primaryForeground"),
  },
  td: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 1.4,
    color: themeColor("textPrimary"),
  },
};

const defaultState: MessageBubbleWidgetState = {
  // Background & Colors (defaults for user message, assistant can override via widgetId)
  bgColor: themeColor("background"), // blueWhite-like for user
  textColor: themeColor("textPrimary"),

  // Border radius
  borderRadius: 23,
  borderRadiusShort: 100, // pill shape for short messages

  // Typography
  fontSize: 16,
  fontWeight: "500",
  fontFamily: "inherit",
  lineHeight: 1.5,

  // Padding
  paddingX: 16,
  paddingY: 12,

  // Single-line height
  singleLineHeight: 46,

  // Layout
  maxWidth: "70%",
  alignment: "left",

  // Short message threshold
  shortMessageThreshold: 50,

  // Streaming cursor (pulsating dot) — defaults to textPrimary
  streamingCursorColor: themeColor("textPrimary"),

  // Element styles with defaults
  elementStyles: DEFAULT_ELEMENT_STYLES,
};

/**
 * Generate edit fields for a single HTML element
 */
const createElementEditFields = (
  element: StylableElement,
  label: string,
  options?: { includeBackgroundColor?: boolean }
): EditField[] => {
  const prefix = `elementStyles.${element}`;
  const fields: EditField[] = [
    {
      key: `${prefix}.fontFamily`,
      label: `${label} Font`,
      type: "font" as const,
    },
    {
      key: `${prefix}.fontSize`,
      label: `${label} Font Size`,
      type: "number" as const,
      min: 10,
      max: 48,
      step: 1,
    },
    {
      key: `${prefix}.fontWeight`,
      label: `${label} Font Weight`,
      type: "options" as const,
      options: FONT_WEIGHT_OPTIONS_NUMERIC,
    },
    {
      key: `${prefix}.color`,
      label: `${label} Color`,
      type: "color" as const,
    },
  ];

  if (options?.includeBackgroundColor) {
    fields.push({
      key: `${prefix}.backgroundColor`,
      label: `${label} Background`,
      type: "color" as const,
    });
  }

  fields.push(
    {
      key: `${prefix}.lineHeight`,
      label: `${label} Line Height`,
      type: "number" as const,
      min: 1,
      max: 2.5,
      step: 0.1,
    },
    {
      key: `${prefix}.marginTop`,
      label: `${label} Margin Top`,
      type: "number" as const,
      min: 0,
      max: 32,
      step: 2,
    },
    {
      key: `${prefix}.marginBottom`,
      label: `${label} Margin Bottom`,
      type: "number" as const,
      min: 0,
      max: 32,
      step: 2,
    }
  );

  return fields;
};

/**
 * Element labels for the editor UI
 */
const ELEMENT_LABELS: Record<StylableElement, string> = {
  h1: "H1",
  h2: "H2",
  h3: "H3",
  h4: "H4",
  h5: "H5",
  p: "Paragraph",
  strong: "Bold",
  em: "Italic",
  code: "Code",
  blockquote: "Blockquote",
  ul: "Unordered List",
  ol: "Ordered List",
  li: "List Item",
  table: "Table",
  thead: "Table Header",
  tbody: "Table Body",
  tr: "Table Row",
  th: "Header Cell",
  td: "Table Cell",
};

// Base edit fields for the message bubble
const baseEditFields: EditField[] = [
  { key: "bgColor", label: "Background Color", type: "color" },
  { key: "textColor", label: "Text Color", type: "color" },
  { key: "streamingCursorColor", label: "Streaming Cursor Color", type: "color" },
  {
    key: "borderRadius",
    label: "Border Radius (px)",
    type: "number",
    min: 0,
    max: 24,
    step: 1,
  },
  {
    key: "borderRadiusShort",
    label: "Short Msg Radius (px)",
    type: "number",
    min: 0,
    max: 100,
    step: 5,
  },
  {
    key: "fontSize",
    label: "Base Font Size (px)",
    type: "number",
    min: 12,
    max: 20,
    step: 1,
  },
  {
    key: "fontWeight",
    label: "Base Font Weight",
    type: "options",
    options: FONT_WEIGHT_OPTIONS_NORMAL_TO_SEMIBOLD,
  },
  {
    key: "fontFamily",
    label: "Font Family",
    type: "font",
  },
  {
    key: "lineHeight",
    label: "Base Line Height",
    type: "number",
    min: 1,
    max: 2,
    step: 0.1,
  },
  {
    key: "paddingX",
    label: "Horizontal Padding (px)",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
  },
  {
    key: "paddingY",
    label: "Vertical Padding (px)",
    type: "number",
    min: 6,
    max: 20,
    step: 2,
  },
  {
    key: "singleLineHeight",
    label: "Single Line Height (px)",
    type: "number",
    min: 0,
    max: 80,
    step: 1,
  },
  {
    key: "maxWidth",
    label: "Max Width",
    type: "options",
    options: [
      { label: "100%", value: "100%" },
      { label: "90%", value: "90%" },
      { label: "80%", value: "80%" },
      { label: "70%", value: "70%" },
      { label: "60%", value: "60%" },
    ],
  },
  {
    key: "alignment",
    label: "Alignment",
    type: "options",
    options: [
      { label: "Left", value: "left" },
      { label: "Right", value: "right" },
      { label: "Center", value: "center" },
    ],
  },
];

// Generate edit fields for all element groups
// For now, include only the most commonly used elements to keep the UI manageable
const headingElements: StylableElement[] = ["h1", "h2", "h3"];
const textElements: StylableElement[] = ["p", "blockquote"];
const listElements: StylableElement[] = ["li"];
const tableElements: StylableElement[] = ["th", "td"];
const tableHeaderElements: StylableElement[] = ["thead"];

const elementEditFields: EditField[] = [
  ...headingElements.flatMap((el) =>
    createElementEditFields(el, ELEMENT_LABELS[el])
  ),
  ...textElements.flatMap((el) =>
    createElementEditFields(el, ELEMENT_LABELS[el])
  ),
  ...listElements.flatMap((el) =>
    createElementEditFields(el, ELEMENT_LABELS[el])
  ),
  ...tableHeaderElements.flatMap((el) =>
    createElementEditFields(el, ELEMENT_LABELS[el], {
      includeBackgroundColor: true,
    })
  ),
  ...tableElements.flatMap((el) =>
    createElementEditFields(el, ELEMENT_LABELS[el])
  ),
];

// Combined edit fields
const editFields: EditField[] = [...baseEditFields, ...elementEditFields];

/**
 * Generate CSS rules for element styles
 * Uses !important to override base styles from animations.css
 */
const generateElementStylesCSS = (
  uniqueClass: string,
  elementStyles: Partial<Record<StylableElement, ElementStyle>> | undefined,
  themeFonts: Record<string, { name: string; url: string; format: string }>,
  resolveColor: ResolveColorFn
): string => {
  if (!elementStyles) return "";

  const cssRules: string[] = [];
  const fontFaces: string[] = [];
  const loadedFonts = new Set<string>();

  for (const [element, style] of Object.entries(elementStyles)) {
    if (!style) continue;

    const properties: string[] = [];

    if (style.fontSize !== undefined) {
      properties.push(`font-size: ${style.fontSize}px`);
    }
    if (style.fontWeight !== undefined) {
      properties.push(`font-weight: ${style.fontWeight}`);
    }
    if (style.color && style.color !== "inherit") {
      const resolvedColor = resolveColor(style.color);
      properties.push(`color: ${resolvedColor}`);
    }
    if (
      style.backgroundColor &&
      style.backgroundColor !== "inherit" &&
      style.backgroundColor !== "transparent"
    ) {
      const resolvedBgColor = resolveColor(style.backgroundColor);
      properties.push(`background-color: ${resolvedBgColor}`);
    }
    if (style.lineHeight !== undefined) {
      properties.push(`line-height: ${style.lineHeight}`);
    }
    if (style.marginTop !== undefined) {
      properties.push(`margin-top: ${style.marginTop}px`);
    }
    if (style.marginBottom !== undefined) {
      properties.push(`margin-bottom: ${style.marginBottom}px`);
    }

    // Handle custom font family
    if (style.fontFamily && style.fontFamily !== "inherit") {
      const fontData = themeFonts[style.fontFamily];
      if (fontData) {
        if (fontData.format === "system") {
          properties.push(`font-family: ${fontData.url}`);
        } else {
          properties.push(`font-family: "${fontData.name}", sans-serif`);

          // Add @font-face for uploaded fonts
          if (fontData.format !== "google" && !loadedFonts.has(fontData.name)) {
            fontFaces.push(`
              @font-face {
                font-family: "${fontData.name}";
                src: url("${fontData.url}") format("${fontData.format}");
                font-display: swap;
              }
            `);
            loadedFonts.add(fontData.name);
          }
        }
      }
    }

    if (properties.length > 0) {
      // Target elements with high specificity to override theme CSS from generateConciergeCSS
      // which uses #concierge-mount selectors. We include both:
      // - #concierge-mount prefix for Concierge Shadow DOM context
      // - Non-prefixed for non-Concierge contexts (e.g., standalone usage)
      cssRules.push(`#concierge-mount .${uniqueClass} .message-content ${element},
#concierge-mount .${uniqueClass} .message-content .message-markdown ${element},
.${uniqueClass} .message-content ${element},
.${uniqueClass} .message-content .message-markdown ${element} {
  ${properties.join(";\n  ")};
}`);
    }
  }

  return [...fontFaces, ...cssRules].join("\n\n");
};

/**
 * Helper to set nested value in object using dot notation (immutable)
 */
const setNestedValue = (obj: any, path: string, value: any): any => {
  const keys = path.split(".");
  const result = { ...obj };
  let current = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    current[key] = { ...current[key] };
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

/**
 * Flatten nested state for EditPanel display
 */
const flattenState = (state: MessageBubbleWidgetState): Record<string, any> => {
  const flat: Record<string, any> = {};

  // Copy top-level properties
  for (const [key, value] of Object.entries(state)) {
    if (
      key === "elementStyles" &&
      typeof value === "object" &&
      value !== null
    ) {
      // Flatten element styles
      for (const [element, styles] of Object.entries(
        value as Record<string, ElementStyle>
      )) {
        if (styles && typeof styles === "object") {
          for (const [prop, propValue] of Object.entries(styles)) {
            flat[`elementStyles.${element}.${prop}`] = propValue;
          }
        }
      }
    } else {
      flat[key] = value;
    }
  }

  return flat;
};

export const MessageBubbleWidget: React.FC<MessageBubbleWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange: baseHandleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<MessageBubbleWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    baseEditFields // Only use base fields for type transformations
  );

  // Custom handleEditChange that supports nested keys (dot notation)
  const handleEditChange = (key: string, value: any) => {
    if (key.includes(".")) {
      // Handle nested key (e.g., "elementStyles.h1.fontSize")
      const newState = setNestedValue(state, key, value);
      // Update through the base handler by setting the top-level key
      const topKey = key.split(".")[0];
      baseHandleEditChange(topKey, newState[topKey]);
    } else {
      // Regular flat key
      baseHandleEditChange(key, value);
    }
  };

  // Flatten state for EditPanel
  const flatEditValues = useMemo(() => flattenState(state), [state]);

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.fontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  // (not inline, because Shadow DOM isolates @font-face declarations)
  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  // Collect all Google Font URLs from element styles
  const elementStyleGoogleFonts = useMemo(() => {
    const urls: string[] = [];
    if (state.elementStyles) {
      for (const style of Object.values(state.elementStyles)) {
        if (style?.fontFamily && style.fontFamily !== "inherit") {
          const fontInfo = themeFonts[style.fontFamily];
          if (
            fontInfo?.format === "google" &&
            fontInfo.url &&
            !urls.includes(fontInfo.url)
          ) {
            urls.push(fontInfo.url);
          }
        }
      }
    }
    return urls;
  }, [state.elementStyles, themeFonts]);

  // Load Google Fonts into document.head when fonts change
  // NOTE: This hook must be called before any early returns to satisfy React's rules of hooks
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
    // Load element style fonts
    for (const url of elementStyleGoogleFonts) {
      loadGoogleFontInHead(url);
    }
  }, [googleFontLink, elementStyleGoogleFonts]);

  // Generate unique class for this widget instance (stable based on widgetId)
  const uniqueClass = `message-bubble-${widgetId || "default"}`;

  // Generate element styles CSS
  const elementStylesCss = useMemo(
    () =>
      generateElementStylesCSS(
        uniqueClass,
        state.elementStyles,
        themeFonts,
        resolveColor
      ),
    [uniqueClass, state.elementStyles, themeFonts, resolveColor]
  );

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  // - system: use the url field which contains the CSS font-family value
  // - google/upload: use the font name
  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url // System fonts store CSS font-family in url field
      : `"${fontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts with actual file URLs)
  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  // Generate @font-face CSS only for uploaded fonts
  // Also include !important override rule for font-family
  const baseFontCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Combine all CSS
  const styleCss = [baseFontCss, elementStylesCss].filter(Boolean).join("\n\n");

  // Create enhanced state with resolved fontFamily CSS
  const enhancedState: MessageBubbleWidgetState & { fontFamilyCss: string } = {
    ...state,
    fontFamilyCss,
  };

  // Render the children with current state and font styles
  const widgetContent = (
    <div className={uniqueClass}>
      {styleCss && <style>{styleCss}</style>}
      {children(enhancedState, resolveColor)}
    </div>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={flatEditValues}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
